import React from "react";
import { Modal, Input, Button, message } from "antd";
import {
  LockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CameraOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { useDropzone } from "react-dropzone";
import {
  userPutChangePassword,
  userPutChangeAvatar,
  usersGetbyToken,
} from "../../../apis";
import { comprimirImagen } from "../../../helpers";
import { useRouterStore } from "../../../stores";

/* ── Header corporativo reutilizable ─────────────────────────────────────── */
const QuickHeader = ({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) => (
  <div className="uq-header">
    <div className="uq-header-icon">{icon}</div>
    <div>
      <div className="uq-header-title">{title}</div>
      <div className="uq-header-sub">{subtitle}</div>
    </div>
  </div>
);

const getInitials = (name?: string | null): string => {
  if (!name) return "U";
  const p = name.trim().split(" ").filter(Boolean);
  return (p.length === 1 ? p[0][0] : p[0][0] + p[1][0]).toUpperCase();
};

/* ═══════════════════════════ CAMBIAR CONTRASEÑA ═══════════════════════════ */
interface PassProps {
  open: boolean;
  onClose: () => void;
}

export const ChangePasswordModal = ({ open, onClose }: PassProps) => {
  const [backPass, setBackPass] = React.useState("");
  const [newPass, setNewPass] = React.useState("");
  const [confPass, setConfPass] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const reset = () => {
    setBackPass("");
    setNewPass("");
    setConfPass("");
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const rules = [
    { ok: newPass.length >= 8, txt: "Mínimo 8 caracteres" },
    { ok: /[A-Za-z]/.test(newPass) && /\d/.test(newPass), txt: "Letras y números" },
    { ok: /[^A-Za-z0-9]/.test(newPass), txt: "Un símbolo (recomendado)" },
  ];
  const score = rules.filter((r) => r.ok).length;
  const strength =
    newPass.length === 0
      ? { label: "", cls: "" }
      : score <= 1
        ? { label: "Débil", cls: "weak" }
        : score === 2
          ? { label: "Media", cls: "medium" }
          : { label: "Fuerte", cls: "strong" };

  const canSave =
    backPass.length > 0 &&
    newPass.length >= 8 &&
    /[A-Za-z]/.test(newPass) &&
    /\d/.test(newPass) &&
    newPass === confPass &&
    !saving;

  const handleSave = async () => {
    if (newPass !== confPass) {
      message.error("Las contraseñas no coinciden");
      return;
    }
    setSaving(true);
    try {
      const { data } = await userPutChangePassword({ backPass, newPass });
      message.success(data ?? "Contraseña actualizada");
      reset();
      onClose();
    } catch (err: any) {
      message.error(err?.message ?? "No se pudo cambiar la contraseña");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={440}
      destroyOnClose
      centered
      className="uq-modal"
      title={
        <QuickHeader
          icon={<LockOutlined />}
          title="Cambiar contraseña"
          subtitle="Protege el acceso a tu cuenta"
        />
      }
    >
      <div className="uq-body">
        <label className="uq-label">Contraseña actual</label>
        <Input.Password
          size="large"
          value={backPass}
          onChange={(e) => setBackPass(e.target.value)}
          placeholder="Tu contraseña vigente"
          iconRender={(v) => (v ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
          autoComplete="current-password"
        />

        <label className="uq-label" style={{ marginTop: 14 }}>
          Nueva contraseña
        </label>
        <Input.Password
          size="large"
          value={newPass}
          onChange={(e) => setNewPass(e.target.value)}
          placeholder="Crea una contraseña segura"
          iconRender={(v) => (v ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
          autoComplete="new-password"
        />

        {newPass.length > 0 && (
          <div className="uq-strength">
            <div className={`uq-strength-bar ${strength.cls}`}>
              <span />
              <span />
              <span />
            </div>
            <span className={`uq-strength-label ${strength.cls}`}>
              {strength.label}
            </span>
          </div>
        )}

        <ul className="uq-rules">
          {rules.map((r, i) => (
            <li key={i} className={r.ok ? "ok" : ""}>
              <CheckCircleFilled /> {r.txt}
            </li>
          ))}
        </ul>

        <label className="uq-label" style={{ marginTop: 6 }}>
          Confirmar nueva contraseña
        </label>
        <Input.Password
          size="large"
          value={confPass}
          onChange={(e) => setConfPass(e.target.value)}
          placeholder="Repite la nueva contraseña"
          status={confPass.length > 0 && confPass !== newPass ? "error" : ""}
          iconRender={(v) => (v ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
          autoComplete="new-password"
        />
        {confPass.length > 0 && confPass !== newPass && (
          <span className="uq-error">Las contraseñas no coinciden</span>
        )}

        <div className="uq-actions">
          <Button onClick={handleClose} disabled={saving} className="uq-btn-ghost">
            Cancelar
          </Button>
          <Button
            type="primary"
            loading={saving}
            disabled={!canSave}
            onClick={handleSave}
            className="uq-btn-primary"
          >
            Guardar cambios
          </Button>
        </div>
      </div>
    </Modal>
  );
};

/* ═══════════════════════════ CAMBIAR AVATAR ══════════════════════════════ */
interface AvatarProps {
  open: boolean;
  onClose: () => void;
}

export const ChangeAvatarModal = ({ open, onClose }: AvatarProps) => {
  const storeAvatar = useRouterStore((s) => s.avatar);
  const firstName = useRouterStore((s) => s.firstName);
  const setAvatar = useRouterStore((s) => s.setAvatar);

  const [file, setFile] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState<string>("");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setFile(null);
      setPreview("");
    }
  }, [open]);

  const onDrop = React.useCallback((accepted: File[]) => {
    const f = accepted[0];
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(f.type)) {
      message.error("Formato no válido. Usa JPG, PNG o WEBP");
      return;
    }
    if (f.size > 4 * 1024 * 1024) {
      message.error("La imagen no puede pesar más de 4 MB");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    maxFiles: 1,
    multiple: false,
  });

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    if (!file) return;
    setSaving(true);
    try {
      const blob = await comprimirImagen(file as unknown as Blob, 30);
      const dataUrl: string = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onloadend = () => resolve(r.result as string);
        r.onerror = reject;
        r.readAsDataURL(blob as unknown as Blob);
      });

      const { data } = await userPutChangeAvatar(dataUrl);
      if (data) message.success(data);

      const { data: fresh } = await usersGetbyToken();
      setAvatar((fresh?.avatar as string) ?? dataUrl);
      onClose();
    } catch (err: any) {
      message.error(err?.message ?? "No se pudo actualizar el avatar");
    } finally {
      setSaving(false);
    }
  };

  const shown = preview || storeAvatar;

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={440}
      destroyOnClose
      centered
      className="uq-modal"
      title={
        <QuickHeader
          icon={<CameraOutlined />}
          title="Cambiar avatar"
          subtitle="Personaliza tu foto de perfil"
        />
      }
    >
      <div className="uq-body">
        <div className="uq-avatar-preview-wrap">
          <div className="uq-avatar-preview">
            {shown ? (
              <img src={shown} alt="avatar" />
            ) : (
              <span>{getInitials(firstName)}</span>
            )}
          </div>
        </div>

        <div
          {...getRootProps()}
          className={`uq-dropzone${isDragActive ? " active" : ""}`}
        >
          <input {...getInputProps()} />
          <CameraOutlined className="uq-dropzone-icon" />
          <div className="uq-dropzone-text">
            {isDragActive
              ? "Suelta la imagen aquí"
              : file
                ? file.name
                : "Arrastra una imagen o haz click"}
          </div>
          <div className="uq-dropzone-hint">JPG, PNG o WEBP · máx. 4 MB</div>
        </div>

        <div className="uq-actions">
          <Button onClick={handleClose} disabled={saving} className="uq-btn-ghost">
            Cancelar
          </Button>
          <Button
            type="primary"
            loading={saving}
            disabled={!file || saving}
            onClick={handleSave}
            className="uq-btn-primary"
          >
            Guardar avatar
          </Button>
        </div>
      </div>
    </Modal>
  );
};
